/**
 * Витринный калькулятор платежа (#1335) — обычный аннуитетный кредит.
 *
 * Отдельно от BF-расчёта в `context/DutyFreeContext.jsx` намеренно. Тот —
 * продукт конкретного банка для сегмента дилера (шар 50%, 23+1 платежа,
 * потолок кредита €12 000) и живёт у дилеров как есть (#1259). На витрине он
 * давал одинаковые €347/mo у Picanto за €16k и Corvette за €109k: всё, что
 * выше потолка, уходило во взнос. Здесь кредит — это цена минус взнос, без
 * потолка, и платёж зависит от машины.
 *
 * Ставка и сроки — демонстрационные: блок помечен на странице как
 * иллюстрация, а не оферта (регуляторика — фаза 2 #1330).
 */
export const SHOWCASE_FINANCE = Object.freeze({
  annualRate: 0.069,
  terms: Object.freeze([24, 36, 48, 60]),
  defaultTerm: 48,
  defaultDepositShare: 0.2,
  depositStep: 100,
  // Кредит меньше этого не берут: без нижней границы ползунок доходил до
  // взноса, равного цене, и блок показывал «€0/mo».
  minLoan: 1000,
});

const roundDownToStep = (value, step) => Math.floor(value / step) * step;

/**
 * Эффективная годовая ставка (APR / TAEG / effektiver Jahreszins) из
 * номинальной при помесячной капитализации и без комиссий: (1 + r/12)^12 − 1.
 * 6,9 % номинальных → 7,12 % годовых. Комиссий в демо-расчёте нет — когда
 * появится банк-партнёр, APR даёт он.
 *
 * @param {number} annualRate — номинальная годовая ставка (0.069 = 6.9%)
 * @returns {number}
 */
export const aprFromNominal = (annualRate) =>
  Math.pow(1 + annualRate / 12, 12) - 1;

/**
 * Ежемесячный платёж по аннуитету, округлённый вверх до евро: занизить
 * платёж покупателю нельзя.
 *
 * @param {number} loan — сумма кредита
 * @param {number} months — срок в месяцах
 * @param {number} annualRate — годовая ставка (0.069 = 6.9%)
 * @returns {number}
 */
export const annuityPayment = (loan, months, annualRate) => {
  if (!(loan > 0) || !(months > 0)) return 0;
  const r = annualRate / 12;
  if (r === 0) return Math.ceil(loan / months);
  return Math.ceil((loan * r) / (1 - Math.pow(1 + r, -months)));
};

/**
 * Допустимый диапазон взноса для цены: от нуля до цены минус минимальный
 * кредит, по шагу ползунка, чтобы его концы были достижимы.
 *
 * @param {number} price
 * @param {typeof SHOWCASE_FINANCE} [options]
 * @returns {{min: number, max: number, initial: number}}
 */
export const depositRange = (price, options = SHOWCASE_FINANCE) => {
  const { depositStep, minLoan, defaultDepositShare } = options;
  if (!(price > 0)) return { min: 0, max: 0, initial: 0 };
  const max = Math.max(0, roundDownToStep(price - minLoan, depositStep));
  const initial = Math.min(
    max,
    roundDownToStep(price * defaultDepositShare, depositStep)
  );
  return { min: 0, max, initial };
};

/**
 * Полный расчёт для разбивки на странице.
 *
 * @param {{price: number, deposit: number, months: number}} input
 * @param {typeof SHOWCASE_FINANCE} [options]
 * @returns {{deposit: number, loan: number, monthly: number, months: number,
 *   totalInterest: number, totalPayable: number}}
 */
export const calculateFinance = (
  { price, deposit, months },
  options = SHOWCASE_FINANCE
) => {
  const { max } = depositRange(price, options);
  const safeDeposit = Math.min(Math.max(deposit || 0, 0), max);
  const loan = price > 0 ? price - safeDeposit : 0;
  const monthly = annuityPayment(loan, months, options.annualRate);
  const repaid = monthly * months;
  return {
    deposit: safeDeposit,
    loan,
    monthly,
    months,
    totalInterest: Math.max(0, repaid - loan),
    totalPayable: safeDeposit + repaid,
  };
};
